/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import {
  EInvoiceRecipientEmail,
  EInvoiceRecipientMail,
  EOrganization,
  ESnapshotExists,
  MailDelivery
} from 'lib/types';
import PlacementActions, {
  ConfirmInvoiceRecipientData,
  selectDefaultInvoiceRecipient,
  selectPurchaseOrderNumber
} from 'redux/placement';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { TextField } from 'lib/components/TextField';
import { safeStringify } from 'lib/utils/stringify';
import { getDisplayName } from 'lib/helpers';
import { Alert } from 'lib/components/Alert';
import { selectIsColumnRep, selectIsPublisher } from 'redux/auth';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { MailedNoticeDocumentType, ShippingTier } from 'lib/types/mail';
import { NoticeAddOnPricingConfig } from 'lib/pricing/noticeAddOns/types';
import {
  EmailOrMailToggle,
  EmailOrMailForm
} from './InvoiceRecipientFormComponents';
import { MailRecipientsManager } from './DeliveryAddOns/MailRecipientsManager';
import { ADDONS_COPY } from './DeliveryAddOns/addOnsCopy';
import { areMailedDocsDisabledForPublisher } from './DeliveryAddOns/pricingConfig';
import { DEFAULT_MAILING } from './NoticeDelivery/deliveryRecipients.helpers';

export const useInvoiceRecipientFormState = () => {
  const dispatch = useAppDispatch();

  const purchaseOrderNumberDraft = useAppSelector(selectPurchaseOrderNumber);
  const invoiceRecipient = useAppSelector(
    state => state.placement.invoiceRecipient
  );
  const defaultInvoiceRecipient = useAppSelector(selectDefaultInvoiceRecipient);

  const invoiceRecipientEmail =
    invoiceRecipient?.type === 'email' ? invoiceRecipient : undefined;
  const invoiceRecipientMail =
    invoiceRecipient?.type === 'mail' ? invoiceRecipient : undefined;

  const defaultAddress = invoiceRecipientMail?.address;
  const [sendToEmail, setSendToEmail] = useState(
    !(invoiceRecipient?.type === 'mail')
  );

  const [billTo, setBillTo] = useState('');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(
    purchaseOrderNumberDraft || ''
  );

  const [email, setEmail] = useState(
    invoiceRecipientEmail?.email || defaultInvoiceRecipient?.email || ''
  );

  const [addressLine1, setAddressLine1] = useState(
    defaultAddress?.address_line1 ?? ''
  );
  const [addressLine2, setAddressLine2] = useState(
    defaultAddress?.address_line2 ?? ''
  );
  const [city, setCity] = useState(defaultAddress?.address_city ?? '');
  const [state, setState] = useState(
    typeof defaultAddress?.address_state === 'string'
      ? defaultAddress?.address_state
      : ''
  );
  const [zip, setZip] = useState(
    invoiceRecipientMail?.address?.address_zip ?? ''
  );

  useEffect(() => {
    if (!defaultInvoiceRecipient) return;
    setEmail(
      invoiceRecipientEmail?.email || defaultInvoiceRecipient.email || ''
    );

    const deprecatedInvoiceRecipientName =
      invoiceRecipient?.organizationName ||
      getDisplayName(invoiceRecipient?.firstName, invoiceRecipient?.lastName);
    setBillTo(
      deprecatedInvoiceRecipientName ||
        invoiceRecipient?.name ||
        defaultInvoiceRecipient.name ||
        ''
    );

    // Don't default fill address information if invoiceRecipient is set to email
    if (!!invoiceRecipient && invoiceRecipient.type === 'email') return;

    // Pre-fill address fields either from the set invoice recipient
    // or from the default
    const entityToPopulateAddress = invoiceRecipientMail?.address.address_line1
      ? invoiceRecipientMail.address
      : defaultInvoiceRecipient.mailingAddress;

    const {
      address_line1,
      address_line2,
      address_city,
      address_state, // This is for some reason saved as Label, not Value
      address_zip
    } = entityToPopulateAddress;

    if (address_line1) {
      setAddressLine1(address_line1);
    }
    if (address_line2) {
      setAddressLine2(address_line2);
    }
    if (address_city) {
      setCity(address_city);
    }
    if (address_state) {
      setState(String(address_state) || '');
    }
    if (address_zip) {
      setZip(address_zip);
    }
  }, [safeStringify(invoiceRecipient), safeStringify(defaultInvoiceRecipient)]);

  const shouldSetInvoiceRecipientOnNotice =
    // If the email has been updated from the default
    (sendToEmail && defaultInvoiceRecipient?.email !== email) ||
    // If the billing name has been updated from the default
    (sendToEmail && defaultInvoiceRecipient?.name !== billTo) ||
    // Should always be set when sending a paper copy
    !sendToEmail;

  const sendToMailingAddress = !sendToEmail;

  const recipientInfoValid = !!billTo;

  // TextField has native validation for email/zip
  // but I'm not sure if it's possible to pull that validation into these checks
  const emailRe = /.+?@.+?\..+/;
  const recipientEmailValid = !sendToEmail || (!!email && emailRe.test(email));

  // When delivery add-ons are enabled the legacy single-address fields are
  // replaced by MailRecipientsManager. Skip validation of those hidden Redux
  // fields; the MailRecipientsManager owns its own validation.
  const enableDeliveryAddons = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_AFFIDAVIT_INVOICE_ADDONS,
    false
  );

  const zipRe = /^[0-9]{5}$/;
  const zipValid = zipRe.test(zip.trim());
  const recipientMailingValid =
    enableDeliveryAddons ||
    !sendToMailingAddress ||
    ([addressLine1, city, state, zip].every(e => !!e) && zipValid);

  const isStepComplete =
    (!shouldSetInvoiceRecipientOnNotice && recipientInfoValid) ||
    // All form fields have valid inputs
    (recipientInfoValid && recipientEmailValid && recipientMailingValid);

  const onExit = () => {
    const hasEditedPurchaseOrderNumber =
      purchaseOrderNumber !== purchaseOrderNumberDraft;
    if (hasEditedPurchaseOrderNumber) {
      dispatch(
        PlacementActions.confirmPurchaseOrderNumber(purchaseOrderNumber)
      );
    }

    if (!shouldSetInvoiceRecipientOnNotice) {
      const data: ConfirmInvoiceRecipientData = {
        invoiceRecipient: null
      };

      dispatch(PlacementActions.confirmInvoiceRecipient(data));
      return;
    }
    // We are deprecating all fields in favor of `name`
    const basicInfo = { name: billTo };

    if (sendToEmail) {
      const invoiceRecipient: EInvoiceRecipientEmail = {
        type: 'email',
        email,
        ...basicInfo
      };

      const data: ConfirmInvoiceRecipientData = {
        invoiceRecipient
      };

      dispatch(PlacementActions.confirmInvoiceRecipient(data));
    } else {
      const invoiceRecipient: EInvoiceRecipientMail = {
        type: 'mail',
        address: {
          address_line1: addressLine1,
          address_line2: addressLine2,
          address_city: city,
          address_state: state,
          address_zip: zip
        },
        ...basicInfo
      };

      const data: ConfirmInvoiceRecipientData = {
        invoiceRecipient
      };

      dispatch(PlacementActions.confirmInvoiceRecipient(data));
    }
  };

  return {
    isStepComplete,
    onExit,
    email,
    setEmail,
    addressLine1,
    setAddressLine1,
    addressLine2,
    setAddressLine2,
    city,
    setCity,
    state,
    setState,
    zip,
    setZip,
    sendToEmail,
    setSendToEmail,
    zipValid,
    recipientEmailValid,
    billTo,
    setBillTo,
    purchaseOrderNumber,
    setPurchaseOrderNumber
  };
};

type InvoiceRecipientFormProps = {
  publisherOrganization?: ESnapshotExists<EOrganization>;
  email: string;
  setEmail: (newValue: string) => void;
  addressLine1: string;
  setAddressLine1: (newValue: string) => void;
  addressLine2: string;
  setAddressLine2: (newValue: string) => void;
  city: string;
  setCity: (newValue: string) => void;
  state: string;
  setState: (newValue: string) => void;
  zip: string;
  setZip: (newValue: string) => void;
  sendToEmail: boolean;
  setSendToEmail: (newValue: boolean) => void;
  billTo: string;
  setBillTo: (newValue: string) => void;
  purchaseOrderNumber: string;
  setPurchaseOrderNumber: (newValue: string) => void;
  invoiceMailingRecipients?: MailDelivery[];
  onInvoiceMailingRecipientsChange?: (recipients: MailDelivery[]) => void;
  pricingConfig?: NoticeAddOnPricingConfig;
  invoiceMailingValidationErrors?: string[];
  showInvoiceMailingErrors?: boolean;
};

export function InvoiceRecipientForm({
  publisherOrganization,
  email,
  setEmail,
  addressLine1,
  setAddressLine1,
  addressLine2,
  setAddressLine2,
  city,
  setCity,
  state,
  setState,
  zip,
  setZip,
  sendToEmail,
  setSendToEmail,
  billTo,
  setBillTo,
  purchaseOrderNumber,
  setPurchaseOrderNumber,
  invoiceMailingRecipients,
  onInvoiceMailingRecipientsChange,
  pricingConfig,
  invoiceMailingValidationErrors,
  showInvoiceMailingErrors
}: InvoiceRecipientFormProps) {
  const isPublisher = useAppSelector(selectIsPublisher);
  const isColumnRep = useAppSelector(selectIsColumnRep);
  const enableDeliveryAddOns = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_AFFIDAVIT_INVOICE_ADDONS,
    false
  );
  const mailedDocsDisabled = areMailedDocsDisabledForPublisher(
    publisherOrganization
  );
  const showInvoiceMailAddOns = enableDeliveryAddOns && !sendToEmail;

  // Under the add-ons flag a mailed invoice saves `invoiceRecipient: null` and
  // lives in the mail subcollection, so `sendToEmail` cannot initialise from it.
  const deliveryChoiceMade = useRef(false);
  const chooseDelivery = (newValue: boolean) => {
    deliveryChoiceMade.current = true;
    setSendToEmail(newValue);
  };
  useEffect(() => {
    if (
      deliveryChoiceMade.current ||
      !sendToEmail ||
      mailedDocsDisabled ||
      !enableDeliveryAddOns
    ) {
      return;
    }
    if ((invoiceMailingRecipients?.length ?? 0) > 0) {
      setSendToEmail(false);
    }
  }, [
    invoiceMailingRecipients,
    sendToEmail,
    mailedDocsDisabled,
    enableDeliveryAddOns,
    setSendToEmail
  ]);

  // A notice saved before the publisher stopped mailing still carries an invoice
  // mail record, which prices and mails on submit though the option is hidden.
  useEffect(() => {
    if (!mailedDocsDisabled) {
      return;
    }
    if (!sendToEmail) {
      setSendToEmail(true);
    }
    if (
      onInvoiceMailingRecipientsChange &&
      (invoiceMailingRecipients?.length ?? 0) > 0
    ) {
      onInvoiceMailingRecipientsChange([]);
    }
  }, [
    mailedDocsDisabled,
    sendToEmail,
    setSendToEmail,
    invoiceMailingRecipients,
    onInvoiceMailingRecipientsChange
  ]);

  // Rep billing guidance: shown to internal reps (Column reps) and external reps
  // (publishers, who in the placement flow always place on behalf of a customer).
  const showFilerHelpers = isColumnRep || isPublisher;

  // Seed/clear invoice mailing recipients when the user toggles invoice mail mode.
  const prevShowInvoiceMailAddOns = useRef(showInvoiceMailAddOns);
  useEffect(() => {
    if (showInvoiceMailAddOns) {
      // Seed a default row so validateDeliveryForm can detect that mail mode
      // is active even if the user hasn't typed anything yet.
      if (
        onInvoiceMailingRecipientsChange &&
        (invoiceMailingRecipients?.length ?? 0) === 0
      ) {
        onInvoiceMailingRecipientsChange(
          [{ ...DEFAULT_MAILING }].map(r => ({
            ...r,
            documentType: MailedNoticeDocumentType.INVOICE,
            shippingTier: ShippingTier.GROUND,
            provider: 'lob' as const
          }))
        );
      }
    } else if (
      prevShowInvoiceMailAddOns.current &&
      onInvoiceMailingRecipientsChange &&
      (invoiceMailingRecipients?.length ?? 0) > 0
    ) {
      // Clear stale recipients when switching back to email delivery.
      onInvoiceMailingRecipientsChange([]);
    }
    prevShowInvoiceMailAddOns.current = showInvoiceMailAddOns;
  }, [
    showInvoiceMailAddOns,
    onInvoiceMailingRecipientsChange,
    invoiceMailingRecipients
  ]);

  function handleInvoiceRecipientsChange(recipients: MailDelivery[]) {
    if (!onInvoiceMailingRecipientsChange) {
      return;
    }
    const stamped = recipients.map(r => ({
      ...r,
      documentType: MailedNoticeDocumentType.INVOICE,
      shippingTier: ShippingTier.GROUND,
      provider: 'lob' as const
    }));
    onInvoiceMailingRecipientsChange(stamped);
  }

  // Pre-stamped template for MailRecipientsManager phantom/new rows so that
  // invoice entries carry the correct documentType from the moment they exist,
  // not only after the first onChange round-trip through handleInvoiceRecipientsChange.
  const invoiceBlankRow: MailDelivery = {
    ...DEFAULT_MAILING,
    documentType: MailedNoticeDocumentType.INVOICE,
    shippingTier: ShippingTier.GROUND,
    provider: 'lob' as const
  };

  return (
    <form>
      <div className="mb-3">
        <Alert
          id="invoice-info"
          icon={<InformationCircleIcon className="w-5" />}
          description={
            !showFilerHelpers
              ? "By default, invoices are addressed to the registered user's name (individual) or organization name (organization member), and sent via e-mail to the user and organization members who have notifications enabled."
              : 'Edit the billing details if someone other than the customer should pay for this notice.'
          }
        />
      </div>
      <div className="space-y-6">
        <TextField
          id="bill-to"
          labelText="Bill to"
          /**
           * Sending mail via Lob errors if the name is longer than 40
           * characters
           */
          maxLength={40}
          required
          onChange={billTo => setBillTo(billTo)}
          value={billTo}
          noteText={
            showFilerHelpers
              ? 'If you wish to address the invoice to another party, edit the Bill To field.'
              : undefined
          }
        />
        <TextField
          id="purchase-order-number"
          labelText="Purchase order number (optional)"
          placeholder="PO Number, Reference Number/Message"
          noteText="This number/message will appear on the invoice for your notice"
          size="sm"
          maxLength={256} // Abitrary just to avoid excessively long strings
          onChange={setPurchaseOrderNumber}
          value={purchaseOrderNumber}
        />
        <EmailOrMailToggle
          hideMailingOptions={mailedDocsDisabled}
          sendToEmail={sendToEmail}
          setSendToEmail={chooseDelivery}
          pricingConfig={pricingConfig}
        />
      </div>
      {/* A mailed invoice can arrive after its due date. Shown once above
          whichever mailing UI is active (legacy fields or the add-ons manager). */}
      {!sendToEmail && (
        <div className="mt-6">
          <Alert
            id="mail-invoice-warning"
            status="warning"
            icon={<ExclamationTriangleIcon className="w-5" />}
            description={ADDONS_COPY.invoiceMailWarning}
          />
        </div>
      )}
      {!showInvoiceMailAddOns && (
        <div className="bg-gray-100 border rounded-md p-6 mt-6">
          <EmailOrMailForm
            email={email}
            setEmail={setEmail}
            sendToEmail={sendToEmail}
            zip={zip}
            setZip={setZip}
            addressLine1={addressLine1}
            setAddressLine1={setAddressLine1}
            addressLine2={addressLine2}
            setAddressLine2={setAddressLine2}
            city={city}
            setCity={setCity}
            state={state}
            setState={setState}
            showFilerHelpers={showFilerHelpers}
          />
        </div>
      )}
      {showInvoiceMailAddOns && (
        <div className="mt-6 space-y-6">
          <MailRecipientsManager
            recipients={invoiceMailingRecipients ?? []}
            onChange={handleInvoiceRecipientsChange}
            errors={
              showInvoiceMailingErrors
                ? invoiceMailingValidationErrors
                : undefined
            }
            blankRow={invoiceBlankRow}
          />
        </div>
      )}
    </form>
  );
}
